import { useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Device", "Intended Use", "Markets", "Software / AI", "Review"];

export default function DeviceWizard() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    device_name: "",
    device_type: "physical",
    intended_use: "",
    patient_contact: "surface",
    markets: [],
    software_involved: false,
    ai_ml_involved: false,
  });

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const toggleMarket = (m) => {
    setForm((f) => ({
      ...f,
      markets: f.markets.includes(m) ? f.markets.filter((x) => x !== m) : [...f.markets, m],
    }));
  };

  const submit = async () => {
    if (!form.device_name || !form.intended_use || form.markets.length === 0) {
      toast.error("Fill required fields");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/devices", form);
      toast.success("Project created");
      nav(`/devices/${data.project_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-4xl">
        <div className="vq-overline mb-2">New Project</div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900 mb-8">
          Device Intake Wizard
        </h1>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2">
          {STEPS.map((label, i) => {
            const active = i === step;
            const done = i < step;
            return (
              <div key={label} className="flex items-center gap-2 shrink-0">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold font-mono ${
                  active ? "bg-slate-900 text-white" : done ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-500"
                }`}>
                  {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </div>
                <span className={`text-sm font-medium ${active ? "text-slate-900" : "text-slate-500"}`}>{label}</span>
                {i < STEPS.length - 1 && <div className="w-8 h-px bg-slate-200 mx-2" />}
              </div>
            );
          })}
        </div>

        <div className="vq-card p-8">
          {step === 0 && (
            <div className="space-y-6">
              <div>
                <Label className="text-sm font-semibold text-slate-700">Device name *</Label>
                <Input
                  data-testid="wizard-device-name"
                  value={form.device_name}
                  onChange={(e) => setForm({ ...form, device_name: e.target.value })}
                  className="mt-1.5 h-11"
                  placeholder="e.g. CardioSense Wearable ECG"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-3 block">Device type</Label>
                <RadioGroup value={form.device_type} onValueChange={(v) => setForm({ ...form, device_type: v })}>
                  {[
                    { v: "physical", label: "Physical device", d: "Hardware-based medical device" },
                    { v: "samd", label: "SaMD (Software as a Medical Device)", d: "Standalone software" },
                    { v: "ivd", label: "IVD (In-Vitro Diagnostic)", d: "Diagnostic devices" },
                    { v: "combination", label: "Combination product", d: "Hardware + software + drug/biologic" },
                  ].map((opt) => (
                    <label key={opt.v} htmlFor={`dt-${opt.v}`} className="flex items-start gap-3 p-3 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50 transition-colors">
                      <RadioGroupItem value={opt.v} id={`dt-${opt.v}`} className="mt-0.5" />
                      <div>
                        <div className="font-semibold text-slate-900 text-sm">{opt.label}</div>
                        <div className="text-xs text-slate-500">{opt.d}</div>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <div>
                <Label className="text-sm font-semibold text-slate-700">Intended use *</Label>
                <Textarea
                  data-testid="wizard-intended-use"
                  value={form.intended_use}
                  onChange={(e) => setForm({ ...form, intended_use: e.target.value })}
                  rows={5}
                  className="mt-1.5"
                  placeholder="Describe the device's intended use, clinical indication, and patient population…"
                />
                <p className="text-xs text-slate-500 mt-2">This drives the classification engine.</p>
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-3 block">Patient contact</Label>
                <RadioGroup value={form.patient_contact} onValueChange={(v) => setForm({ ...form, patient_contact: v })}>
                  {[
                    { v: "none", label: "None (external / non-contact)" },
                    { v: "surface", label: "Surface contact (skin, intact mucous membranes)" },
                    { v: "invasive", label: "Invasive (short-term)" },
                    { v: "implant", label: "Implantable / long-term invasive" },
                  ].map((opt) => (
                    <label key={opt.v} htmlFor={`pc-${opt.v}`} className="flex items-center gap-3 p-3 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50">
                      <RadioGroupItem value={opt.v} id={`pc-${opt.v}`} />
                      <span className="text-sm font-medium text-slate-900">{opt.label}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <Label className="text-sm font-semibold text-slate-700">Target markets *</Label>
              <p className="text-xs text-slate-500 mb-3">Select all applicable regulatory jurisdictions.</p>
              {[
                { m: "FDA", label: "United States (FDA)", d: "21 CFR Part 820, 510(k) / De Novo / PMA" },
                { m: "EU", label: "European Union (EU MDR / IVDR)", d: "Regulation 2017/745 & 2017/746" },
                { m: "India", label: "India (CDSCO)", d: "MDR 2017, Class A / B / C / D" },
                { m: "MDSAP", label: "MDSAP (Multi-market)", d: "US, Canada, Brazil, Japan, Australia" },
              ].map((x) => (
                <label key={x.m} className="flex items-start gap-3 p-4 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50">
                  <Checkbox
                    data-testid={`wizard-market-${x.m}`}
                    checked={form.markets.includes(x.m)}
                    onCheckedChange={() => toggleMarket(x.m)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="font-semibold text-slate-900 text-sm">{x.label}</div>
                    <div className="text-xs text-slate-500 font-mono mt-0.5">{x.d}</div>
                  </div>
                </label>
              ))}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <Label className="text-sm font-semibold text-slate-700">Software & AI/ML</Label>
              <label className="flex items-start gap-3 p-4 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50">
                <Checkbox
                  data-testid="wizard-software"
                  checked={form.software_involved}
                  onCheckedChange={(v) => setForm({ ...form, software_involved: !!v })}
                  className="mt-0.5"
                />
                <div>
                  <div className="font-semibold text-slate-900 text-sm">Software is part of this device</div>
                  <div className="text-xs text-slate-500">Triggers IEC 62304 requirements</div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-4 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50">
                <Checkbox
                  data-testid="wizard-ai"
                  checked={form.ai_ml_involved}
                  onCheckedChange={(v) => setForm({ ...form, ai_ml_involved: !!v })}
                  className="mt-0.5"
                />
                <div>
                  <div className="font-semibold text-slate-900 text-sm">AI / ML component</div>
                  <div className="text-xs text-slate-500">Triggers FDA AI/ML Action Plan & EU AI Act considerations</div>
                </div>
              </label>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <h3 className="font-display font-bold text-lg text-slate-900">Review</h3>
              <div className="space-y-3 text-sm">
                <Row label="Device name" val={form.device_name} />
                <Row label="Type" val={form.device_type} />
                <Row label="Intended use" val={form.intended_use} />
                <Row label="Patient contact" val={form.patient_contact} />
                <Row label="Markets" val={form.markets.join(", ")} />
                <Row label="Software" val={form.software_involved ? "Yes" : "No"} />
                <Row label="AI/ML" val={form.ai_ml_involved ? "Yes" : "No"} />
              </div>
            </div>
          )}

          {/* Nav */}
          <div className="flex justify-between mt-10 pt-6 border-t border-slate-200">
            <Button
              variant="outline"
              onClick={back}
              disabled={step === 0}
              data-testid="wizard-back-btn"
              className="border-slate-300 font-semibold"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={next} data-testid="wizard-next-btn" className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
                Next <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={saving} data-testid="wizard-submit-btn" className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
                {saving ? "Creating…" : "Create project"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Row({ label, val }) {
  return (
    <div className="flex gap-4 border-b border-slate-100 pb-2">
      <div className="w-32 text-xs font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className="flex-1 text-slate-900">{val || <span className="text-slate-400">—</span>}</div>
    </div>
  );
}
